import React, { useState } from 'react';
import { MetricScore, getPerformanceLabelText } from '../utils/kinematicsScoring';
import { METRIC_DEFS } from '../config/kinematicsBenchmarks';
import ConfidenceTag from './ConfidenceTag';
import './MetricDetailsTable.css';

interface MetricDetailsTableProps {
  metricScores: MetricScore[];
}

const MetricDetailsTable: React.FC<MetricDetailsTableProps> = ({ metricScores }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#4caf50'; // Green for HIGH
    if (score >= 50) return '#ffc107'; // Yellow for MEDIUM
    return '#f44336'; // Red for LOW
  };

  const getRangeStatusText = (rangeStatus: string) => {
    switch (rangeStatus) {
      case 'BELOW_RANGE':
        return 'Below ideal';
      case 'IN_RANGE':
        return 'In ideal';
      case 'ABOVE_RANGE':
        return 'Above ideal';
      default:
        return rangeStatus;
    }
  };

  const getRangeStatusColor = (rangeStatus: string) => {
    switch (rangeStatus) {
      case 'IN_RANGE':
        return '#4caf50'; // Green
      case 'BELOW_RANGE':
      case 'ABOVE_RANGE':
        return '#ff9800'; // Orange
      default:
        return '#757575'; // Gray
    }
  };

  const formatValue = (value: number, unit: string) => {
    if (unit === '°') {
      return `${value.toFixed(1)}°`;
    } else if (unit === 'm/s') {
      return `${value.toFixed(1)} m/s`;
    } else if (unit === 'ms') {
      return `${value.toFixed(1)} ms`;
    }
    return `${value.toFixed(1)} ${unit}`;
  };

  return (
    <div className="metric-details-container">
      <button 
        className="expand-button"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>{isExpanded ? '▼' : '▶'}</span>
        <span>Metric Details</span>
        <span className="metric-count">({metricScores.length} metrics)</span>
      </button>

      {isExpanded && (
        <div className="metric-table-wrapper">
          <table className="metric-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Ideal Range</th>
                <th>Performance</th>
                <th>Measurement Confidence</th>
                <th>Tooltip</th>
              </tr>
            </thead>
            <tbody>
              {metricScores.map((metric) => {
                const def = METRIC_DEFS[metric.paramKey];
                const isProxyOrPlaceholder = metric.label === "PROXY" || metric.label === "PLACEHOLDER";
                const performanceLabel = isProxyOrPlaceholder ? metric.label : getPerformanceLabelText(metric.label);
                
                return (
                  <tr key={metric.paramKey} className={metric.rangeStatus === 'IN_RANGE' ? 'in-range' : 'out-of-range'}>
                    <td className="metric-label">{def.label}</td>
                    <td className="metric-value">
                      {formatValue(metric.value, def.unit)}
                    </td>
                    <td className="metric-range">
                      {formatValue(metric.benchmark.low, def.unit)} - {formatValue(metric.benchmark.high, def.unit)}
                    </td>
                    <td className="metric-performance">
                      <div className="performance-section">
                        <div className="performance-header">
                          <strong>Performance</strong>
                          <span className="tooltip-icon" title="How close you are to the ideal biomechanical range.">ℹ️</span>
                        </div>
                        <div className="performance-content">
                          <div className="performance-score">
                            Score: <span style={{ color: getScoreColor(metric.score), fontWeight: 'bold' }}>
                              {metric.score.toFixed(1)}
                            </span>
                          </div>
                          {isProxyOrPlaceholder ? (
                            <div className="performance-label" style={{ fontWeight: 'bold', color: '#ff9800' }}>
                              {performanceLabel}
                            </div>
                          ) : (
                            <div className="performance-label" style={{ 
                              color: getScoreColor(metric.score),
                              fontWeight: 'bold'
                            }}>
                              {performanceLabel}
                            </div>
                          )}
                          <div className="performance-range-status" style={{ 
                            color: getRangeStatusColor(metric.rangeStatus),
                            fontSize: '0.9em',
                            marginTop: '4px'
                          }}>
                            {getRangeStatusText(metric.rangeStatus)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="metric-confidence">
                      <div className="confidence-section">
                        <div className="confidence-header">
                          <strong>Measurement confidence</strong>
                        </div>
                        <div className="confidence-content">
                          <ConfidenceTag tag={def.confidenceTag} />
                        </div>
                      </div>
                    </td>
                    <td className="metric-tooltip" title={def.shortTooltip}>
                      <span className="tooltip-icon">ℹ️</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MetricDetailsTable;




